<template>
  <v-container fluid class="pa-2">
    <v-card outlined class="compact-card">
      <v-card-text class="pa-3">

        <!-- Month selector - Compact -->
        <v-row v-if="availableMonths.length && !loading" class="mt-1 mb-3">
          <v-col cols="12">
            <v-chip-group
              v-model="selectedMonthIndex"
              active-class="primary white--text"
              mandatory
              show-arrows
              dense
            >
              <v-chip
                v-for="(month, i) in availableMonths"
                :key="month.key"
                :value="i"
                class="caption"
                small
              >
                {{ month.label }}
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="48" width="4" />
          <div class="mt-3 caption grey--text text--darken-1">
            Loading rent roll data...
          </div>
        </div>

        <template v-else>
          <template v-if="flatData?.length">
            <!-- Filters + Export - Compact -->
            <v-row class="mb-3" align="center">
              <v-col cols="12" md="8">
                <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                  <v-select
                    v-model="selectedUnit"
                    :items="unitOptions"
                    label="All Units"
                    outlined
                    dense
                    hide-details
                    clearable
                    style="max-width: 180px;"
                    class="small-select"
                    :disabled="loading"
                  />

                  <v-btn-toggle v-model="sortMode" mandatory dense small :disabled="loading">
                    <v-btn small value="reference" class="caption">Ref</v-btn>
                    <v-btn small value="unit" class="caption">Unit</v-btn>
                  </v-btn-toggle>
                </div>
              </v-col>

              <v-col cols="12" md="4" class="text-right">
                <v-btn
                  color="error"
                  dark
                  small
                  @click="generatePdf"
                  :loading="generatingPdf"
                  :disabled="!filteredFlatData.length || loading"
                  class="caption"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  Export PDF
                </v-btn>
              </v-col>
            </v-row>

            <!-- Printable area -->
            <div id="print-content">
              <div class="text-center mb-4">
                <img
                  v-if="base64log"
                  :src="`data:image/png;base64,${base64log}`"
                  alt="Company Logo"
                  style="max-height: 100px; max-width: 100px; object-fit: contain;"
                />
                <h5 class="mt-3 mb-1 font-weight-bold">{{ report.title }}</h5>
                <p v-if="selectedMonthLabel" class="caption mb-0">
                  <strong>Month:</strong> {{ selectedMonthLabel }}
                </p>
                <hr style="margin: 12px 0; border-color: #ddd;" />
              </div>

              <div class="table-responsive">
                <table class="spreadsheet-table">
                  <thead>
                    <tr>
                      <th class="caption font-weight-bold text-left">REF</th>
                      <th class="caption font-weight-bold text-left">PROPERTY - UNIT - TENANT</th>
                      <th v-for="col in visibleMoneyColumns" :key="col.key" class="caption font-weight-bold text-right">
                        {{ col.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in filteredFlatData" :key="row.id">
                      <td class="caption text-left font-weight-medium">{{ row.reference }}</td>
                      <td class="caption text-left">
                        {{ row.property }} - Unit {{ row.unit || '—' }} - {{ row.tenantName || 'Unknown' }}
                      </td>
                      <td
                        v-for="col in visibleMoneyColumns"
                        :key="col.key"
                        class="caption text-right"
                      >
                        {{ formatCurrency(row[col.key]) }}
                      </td>
                    </tr>

                    <tr class="grand-total">
                      <td colspan="2" class="caption text-left pl-4 font-weight-bold">TOTAL</td>
                      <td
                        v-for="col in visibleMoneyColumns"
                        :key="col.key"
                        class="caption text-right font-weight-bold"
                      >
                        {{ formatCurrency(grandTotals[col.key]) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

          <div v-else class="text-center py-8 grey--text text--darken-1 caption">
            No transactions found
          </div>
        </template>

      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const db = getFirestore();

export default {
  name: 'RentRollAllMonths',

  props: {
    property:  { type: Object, default: null },
    properties: { type: Array, default: () => [] },
    unit:      { type: Object, default: null }
  },

  data: () => ({
    report: { title: 'Detailed Ledger' },
    base64log: '',
    flatData: [],
    sortMode: 'unit',
    selectedUnit: null,
    selectedMonthIndex: null,
    unitOptions: [{ text: 'All Units', value: null }],
    generatingPdf: false,
    loading: false,

    moneyColumns: [
      { key: 'rentPaid',            label: 'Rent Paid' },
      { key: 'waterPaid',           label: 'Water Paid' },
      { key: 'waterAv',             label: 'Water Av' },
      { key: 'seweragePaid',        label: 'Sewerage Paid' },
      { key: 'sewerageAv',          label: 'Sewerage Av' },
      { key: 'refusePaid',          label: 'Refuse' },
      { key: 'adminPaid',           label: 'Admin Paid' },
      { key: 'advancePayment',      label: 'Advance Payment' },
      { key: 'leaseFees',           label: 'Lease Fees' },
      { key: 'arrearsCollected',    label: 'Arrears Collected' },
      { key: 'parkingPaid',         label: 'Parking Paid' },
      { key: 'insurance',           label: 'Insurance' },
      { key: 'legalExpenses',       label: 'Legal Expenses' },
      { key: 'repairsMaintenance',  label: 'Repairs & Maintenance' },
      { key: 'utilities',           label: 'Utilities' },
      { key: 'otherVendorExpenses', label: 'Other Vendor Expenses' },
      { key: 'otherCharge',         label: 'Other Charge' },
    ],
  }),

  computed: {
    availableMonths() {
      if (!this.flatData.length) return [];
      const months = new Set(this.flatData.map(r => r.month));
      return [...months]
        .sort((a, b) => new Date(a) - new Date(b))
        .map(m => ({ key: m, label: m }));
    },

    selectedMonthLabel() {
      return this.selectedMonthIndex !== null
        ? this.availableMonths[this.selectedMonthIndex]?.label
        : '';
    },

    filteredFlatData() {
      let rows = [...this.flatData];
      if (this.selectedMonthIndex !== null) {
        const key = this.availableMonths[this.selectedMonthIndex]?.key;
        if (key) rows = rows.filter(r => r.month === key);
      }
      if (this.selectedUnit) rows = rows.filter(r => r.unit === this.selectedUnit);

      if (this.sortMode === 'unit') {
        rows.sort((a, b) => (Number(a.unit) || 999999) - (Number(b.unit) || 999999));
      } else {
        rows.sort((a, b) => a.reference.localeCompare(b.reference));
      }
      return rows;
    },

    grandTotals() {
      const init = this.moneyColumns.reduce((acc, c) => ({ ...acc, [c.key]: 0 }), {});
      return this.filteredFlatData.reduce((acc, row) => {
        this.moneyColumns.forEach(c => {
          acc[c.key] += Number(row[c.key] || 0);
        });
        return acc;
      }, init);
    },

    visibleMoneyColumns() {
      return this.moneyColumns.filter(col => Number(this.grandTotals[col.key]) > 0);
    }
  },

  watch: {
    properties: { handler() { this.loadData(); }, deep: true, immediate: true },
    flatData: {
      immediate: true,
      handler() {
        if (this.availableMonths.length && this.selectedMonthIndex === null) {
          const now = new Date();
          const current = now.toLocaleString('default', { month: 'long' }) + ' ' + now.getFullYear();
          let idx = this.availableMonths.findIndex(m => m.key === current);
          if (idx === -1) idx = this.availableMonths.length - 1;
          this.selectedMonthIndex = idx;
        }
      }
    }
  },

  async created() {
    await this.fetchLogo();
  },

  methods: {
    async fetchLogo() {
      try {
        const q = query(collection(db, 'Logo'), where('id', '==', 2));
        const snap = await getDocs(q);
        if (snap.empty) return;

        const data = snap.docs[0].data();
        let logo = (data.logo2 || data.logo || '').trim()
          .replace(/^data:image\/[a-z]+;base64,/, '')
          .replace(/\s+/g, '');

        if (logo.length > 100) this.base64log = logo;
      } catch (err) {
        console.error('Logo fetch error:', err);
      }
    },

    createZeroRow() {
      return this.moneyColumns.reduce((acc, c) => ({ ...acc, [c.key]: 0 }), {});
    },

    async loadData() {
      this.loading = true;
      try {
        const props = this.properties.length ? this.properties : this.property ? [this.property] : [];
        if (!props.length) return (this.flatData = []);

        const flat = [];

        for (const prop of props) {
          let q = query(collection(db, 'Leases'), where('propertyId', '==', prop.id));
          if (this.unit?.unitName) q = query(q, where('address', '==', this.unit.unitName));

          const leasesSnap = await getDocs(q);

          for (const leaseDoc of leasesSnap.docs) {
            const d = leaseDoc.data();
            const ref = d.leaseReference;
            const tenant = d.tenantName || 'Unknown';
            const unit = d.address?.includes('Unit') ? d.address.split('Unit')[1]?.trim() : '';

            const txQ = query(
              collection(db, 'Transactions'),
              where('leaseReference', '==', ref),
              orderBy('datePaid', 'asc')
            );

            const txSnap = await getDocs(txQ);
            const byMonth = {};

            txSnap.forEach(doc => {
              const t = doc.data();
              const date = new Date(t.datePaid);
              const monthKey = date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear();

              byMonth[monthKey] = byMonth[monthKey] || this.createZeroRow();

              const amt = Number(t.allocatedAmount) || 0;
              if (amt <= 0) return;

              const desc = (t.description || '').toLowerCase().trim();

              if (/rent|monthly rent/i.test(desc)) byMonth[monthKey].rentPaid += amt;
              else if (/water/i.test(desc) && !/av|availability/i.test(desc)) byMonth[monthKey].waterPaid += amt;
              else if (/water.*av|availability/i.test(desc)) byMonth[monthKey].waterAv += amt;
              else if (/sewerage/i.test(desc) && !/av|availability/i.test(desc)) byMonth[monthKey].seweragePaid += amt;
              else if (/sewerage.*av|availability/i.test(desc)) byMonth[monthKey].sewerageAv += amt;
              else if (/refuse/i.test(desc)) byMonth[monthKey].refusePaid += amt;
              else if (/admin/i.test(desc)) byMonth[monthKey].adminPaid += amt;
              else if (/advance/i.test(desc)) byMonth[monthKey].advancePayment += amt;
              else if (/lease.*fee/i.test(desc)) byMonth[monthKey].leaseFees += amt;
              else if (/arrears/i.test(desc)) byMonth[monthKey].arrearsCollected += amt;
              else if (/parking/i.test(desc)) byMonth[monthKey].parkingPaid += amt;
              else if (/insurance/i.test(desc)) byMonth[monthKey].insurance += amt;
              else if (/legal/i.test(desc)) byMonth[monthKey].legalExpenses += amt;
              else if (/repair|maintenance/i.test(desc)) byMonth[monthKey].repairsMaintenance += amt;
              else if (/utilit(y|ies)/i.test(desc)) byMonth[monthKey].utilities += amt;
              else if (/vendor|supplier/i.test(desc)) byMonth[monthKey].otherVendorExpenses += amt;
              else byMonth[monthKey].otherCharge += amt;
            });

            Object.entries(byMonth).forEach(([month, values]) => {
              flat.push({
                id: `${ref}-${month}`,
                month,
                reference: ref,
                property: prop.displayName || 'Property',
                unit,
                tenantName: tenant,
                ...values
              });
            });
          }
        }

        this.flatData = flat;
        this.updateUnitOptions();
      } catch (err) {
        console.error('Rent roll load error:', err);
      } finally {
        this.loading = false;
      }
    },

    updateUnitOptions() {
      const units = [...new Set(this.flatData.map(r => r.unit).filter(Boolean))].sort((a,b) => Number(a) - Number(b));
      this.unitOptions = [
        { text: 'All Units', value: null },
        ...units.map(u => ({ text: `Unit ${u}`, value: u }))
      ];
    },

    formatCurrency(v) {
      if (v == null || isNaN(v) || Number(v) === 0) return '—';

      const num = Number(v);
      const abs = Math.abs(num);
      const sign = num < 0 ? '-' : '';

      const parts = abs.toFixed(2).split('.');
      const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      const decimal = parts[1];

      return `R ${sign}${integer},${decimal}`;
    },

    async generatePdf() {
      if (!this.filteredFlatData?.length) return;

      this.generatingPdf = true;

      try {
        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 40;
        let y = 35;

        // ── Compact Header ───────────────────────────────
        if (this.base64log) {
          try {
            const logoSrc = this.base64log.startsWith('data:')
              ? this.base64log
              : `data:image/png;base64,${this.base64log}`;

            const logoWidth = 80;
            const logoHeight = 25;
            const logoX = (pageWidth - logoWidth) / 2;

            doc.addImage(logoSrc, 'PNG', logoX, y, logoWidth, logoHeight);
            y += logoHeight + 15;
          } catch (e) {
            console.warn('Logo failed in PDF', e);
            y += 30;
          }
        }

        let headerTitle = "Detailed Ledger";

        if (this.properties?.length > 1) {
          headerTitle += " - Multiple Properties";
        } else if (this.property?.displayName) {
          headerTitle += ` - ${this.property.displayName}`;
        } else if (this.landlord?.name) {
          headerTitle += ` - Landlord: ${this.landlord.name}`;
        }

        doc.setFontSize(16);
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "bold");
        doc.text(headerTitle, pageWidth / 2, y, { align: 'center' });
        y += 22;

        const monthLabel = this.selectedMonthLabel || this.selectedMonth || '';
        if (monthLabel) {
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(85, 85, 85);
          doc.text(`Month: ${monthLabel}`, pageWidth / 2, y, { align: 'center' });
          y += 16;
        }

        doc.setLineWidth(0.7);
        doc.setDrawColor(180, 180, 180);
        doc.line(margin, y, pageWidth - margin, y);
        y += 20;

        // ── Table ─────────────────────────────────────────
        const tableColumns = [
          { header: 'REF', dataKey: 'reference' },
          { header: 'PROPERTY - UNIT - TENANT', dataKey: 'tenantDesc' },
          ...this.visibleMoneyColumns.map(col => ({
            header: col.label,
            dataKey: col.key
          }))
        ];

        const tableBody = this.filteredFlatData.map(row => {
          const rowData = {
            reference: row.reference || '',
            tenantDesc: `${row.property} - Unit ${row.unit || '—'} - ${row.tenantName || 'Unknown'}`
          };

          this.visibleMoneyColumns.forEach(col => {
            rowData[col.key] = this.formatCurrency(Number(row[col.key] || 0));
          });

          return rowData;
        });

        const totalRow = {
          reference: 'TOTAL',
          tenantDesc: ''
        };

        this.visibleMoneyColumns.forEach(col => {
          totalRow[col.key] = this.formatCurrency(Number(this.grandTotals[col.key] || 0));
        });

        tableBody.push(totalRow);

        autoTable(doc, {
          columns: tableColumns,
          body: tableBody,
          startY: y,
          theme: 'grid',
          margin: { left: margin, right: margin },
          styles: {
            fontSize: 8,
            cellPadding: 5,
            lineColor: [180, 180, 180],
            lineWidth: 0.3,
            overflow: 'linebreak'
          },
          headStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 7.5
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          columnStyles: {
            reference: { cellWidth: 60, halign: 'left' },
            tenantDesc: { cellWidth: 180, halign: 'left' }
          },
          didParseCell: (data) => {
            if (data.column.dataKey !== 'reference' && data.column.dataKey !== 'tenantDesc') {
              data.cell.styles.halign = 'right';
            }
            if (data.row.index === tableBody.length - 1) {
              data.cell.styles.fillColor = [210, 230, 245];
              data.cell.styles.fontStyle = 'bold';
            }
          },
          didDrawPage: () => {
            doc.setFontSize(6);
            doc.setTextColor(120);
            doc.text(
              `Page ${doc.internal.getNumberOfPages()} • ${new Date().toLocaleDateString('en-ZA')}`,
              pageWidth - margin - 10,
              doc.internal.pageSize.height - 15,
              { align: 'right' }
            );
          }
        });

        const safeMonth = (monthLabel || 'All_Months').replace(/[^a-zA-Z0-9]/g, '_');
        
        let filePrefix = 'Detailed_Ledger';
        if (this.properties?.length > 1) {
          filePrefix = 'Detailed_Ledger_Multiple';
        } else if (this.property?.displayName) {
          filePrefix = `Detailed_Ledger_${this.property.displayName.replace(/[^a-zA-Z0-9]/g, '_')}`;
        } else if (this.landlord?.name) {
          filePrefix = `Detailed_Ledger_${this.landlord.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
        }

        doc.save(`${filePrefix}_${safeMonth}_${new Date().toISOString().slice(0,10)}.pdf`);

      } catch (err) {
        console.error('PDF generation failed:', err);
        alert('Failed to generate PDF. Check console for details.');
      } finally {
        this.generatingPdf = false;
      }
    }
  }
};
</script>

<style scoped>
/* Compact Card */
.compact-card {
  border-radius: 12px !important;
}

/* Spreadsheet Table */
.spreadsheet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem !important;
}

.spreadsheet-table th,
.spreadsheet-table td {
  border: 1px solid #ddd;
  padding: 5px 8px !important;
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.spreadsheet-table th {
  background-color: #f0f0f0 !important;
  font-weight: 700 !important;
  color: #000000 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-size: 0.6rem !important;
  border-bottom: 2px solid #ccc !important;
}

.spreadsheet-table td:first-child,
.spreadsheet-table td:nth-child(2) {
  text-align: left;
}

.spreadsheet-table .grand-total {
  background-color: #e3f2fd !important;
}

.spreadsheet-table .grand-total td {
  border-top: 2px solid #999 !important;
  font-weight: 700 !important;
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Small text field */
.small-select :deep(.v-input__slot) {
  min-height: 36px !important;
}

.small-select :deep(.v-label) {
  font-size: 0.7rem !important;
  color: #000000 !important;
}

.small-select :deep(.v-select__selection) {
  font-size: 0.7rem !important;
}

/* Caption class */
.caption {
  font-size: 0.7rem !important;
}

h5 {
  font-size: 0.9rem !important;
  color: #000000 !important;
}

/* Chip group */
:deep(.v-chip) {
  font-size: 0.65rem !important;
  height: 26px !important;
}

:deep(.v-chip-group .v-chip) {
  margin: 2px !important;
}

/* Buttons */
.v-btn.caption {
  font-size: 0.65rem !important;
  letter-spacing: 0.3px;
  min-height: 30px !important;
  padding: 0 12px !important;
  text-transform: none !important;
}

.v-btn .v-icon {
  font-size: 16px !important;
}

/* Responsive */
@media (max-width: 768px) {
  .spreadsheet-table th,
  .spreadsheet-table td {
    font-size: 0.55rem !important;
    padding: 3px 4px !important;
  }
  
  .spreadsheet-table th {
    font-size: 0.5rem !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-chip-group {
    display: none !important;
  }
}
</style>